import { create } from "zustand";
import { saveAs } from "file-saver";
import {
  ApolloClient,
  ApolloQueryResult,
  ObservableQuery,
  ObservableSubscription,
} from "@apollo/client";
import * as types from "_graphql-types-frontend/graphql";
import { gql } from "_graphql-types-frontend";
import { dynamicgql } from "../../../utils/graphql";
import uuid from "../../../utils/uuid";
import { useCurrentUser } from "../../../utils/hooks";
import { getTemplateConfigForOwner } from "./config";
import { registerTemplateFieldRefetch } from "../refetchTemplateFields";
import {
  ContentItem,
  Field,
  FieldInput,
  FieldState,
  TemplateParams,
  TemplateStore,
} from "../types";

export const NO_QUERY = dynamicgql`
  query noQuery {
    nothing
  }
`;

/**
 * True when any owner in the input map actually has fields to write.
 *
 * #4889: the owner configs return `null` from saveFieldsQuery/deleteFieldsQuery
 * for TWO different reasons — "nothing to write" and "template data not loaded
 * yet" — and the store rejected on both. Only the second is a fault. Screening
 * the empty case out here keeps a null query meaning the real failure, so we
 * don't fail-open and silently swallow a save whose data never loaded.
 */
const hasFieldsToWrite = (
  inputData: Record<string, Record<string, unknown>> | undefined
) =>
  Object.values(inputData ?? {}).some(
    owner => Object.keys(owner ?? {}).length > 0
  );

const RANGER_DUE_DILIGENCE_DRAFTS = gql(/* GraphQL */ `
  mutation rangerDueDilligenceDrafts(
    $fieldKeys: [String!]!
    $investmentId: Int!
    $customPromptsInput: [CustomPromptsInput!]
    $ragLimits: Int
  ) {
    rangerDueDilligenceDrafts(
      investmentId: $investmentId
      fieldKeys: $fieldKeys
      customPromptsInput: $customPromptsInput
      retain: true
      ragLimits: $ragLimits
    ) {
      id
    }
  }
`);

const GET_ONE_ASYNC_TASK = gql(/* GraphQL */ `
  query GetAsyncTaskStatus($id: ID!) {
    asyncTaskStatus(id: $id) {
      id
      endDate
      error
    }
  }
`);

const GET_RANGER_DUE_DILIGENCE_DRAFTS = gql(/* GraphQL */ `
  query rangerDueDiligenceDraftsReturn($id: ID!) {
    getRangerDueDiligenceDrafts(id: $id)
  }
`);

const GET_DEFAULT_PROMPT = gql(/* GraphQL */ `
  query StoredPromptFromFieldKey($fieldKey: String!) {
    storedPromptFromFieldKey(fieldKey: $fieldKey) {
      id
      prompt
    }
  }
`);

const defaultGetModifyDate = () => new Date();

// Enhanced store creation with better cleanup management
interface CreateTemplateStoreProps {
  params: TemplateParams;
  client: ApolloClient<any>;
  currentUser: ReturnType<typeof useCurrentUser>;
}

export const createTemplateStore = ({
  params,
  client,
  currentUser,
}: CreateTemplateStoreProps) =>
  create<TemplateStore>((set, get) => {
    const config = getTemplateConfigForOwner(params.ownerType);
    let querySubscriptions: ObservableSubscription[] = [];
    let deregisterRefetch: (() => void) | null = null;
    let isDestroyed = false;
    let isInitialized = false;

    /**
     * Expose these observables to out-of-tree callers (agent chat, mutation
     * plan card) so a server-side field write can refresh the open form. Any
     * previous registration is dropped first — fetchFieldData re-runs on tab
     * and period changes and builds a fresh set of observables each time.
     */
    const registerRefetch = (observables: ObservableQuery<any>[]) => {
      deregisterRefetch?.();
      deregisterRefetch = registerTemplateFieldRefetch(() => {
        observables.forEach(observable => {
          // A refetch that races a teardown rejects; nothing to recover.
          observable.refetch().catch(() => undefined);
        });
      });
    };

    const cleanup = () => {
      isDestroyed = true;
      deregisterRefetch?.();
      deregisterRefetch = null;
      querySubscriptions.forEach(sub => {
        try {
          sub.unsubscribe();
        } catch (error) {
          console.warn("Error unsubscribing from query:", error);
        }
      });
      querySubscriptions = [];
    };

    const safeSet = (updater: any) => {
      if (!isDestroyed) {
        // Apply the state update
        set(updater);
      }
    };

    return {
      // Basic state properties
      displayName: "",
      name: params.name,
      hideImageUpload: false,
      content: [],
      error: undefined,
      loading: false,
      saving: false,
      ownerType: params.ownerType,
      ownerId: params.ownerId,
      entityOwnerIds: {},
      editMode: params.editMode,
      period: params.period,
      defaultVersion: undefined,
      availableVersions: [],
      templateVersion: undefined,
      formDataValues: null,
      publishedData: null,
      draftData: null,
      requiredFields: [],
      persistingFields: {},
      templateData: null,
      templateQueryData: null,
      publishedFieldData: null,
      managerFieldData: null,
      rangerFieldData: null,
      editFieldData: null,
      cleanup,
      // State update methods
      setSaving: (state: boolean) => safeSet({ saving: state }),
      setEntityOwnerIds: ids => safeSet({ entityOwnerIds: ids }),
      // Template version management
      changeTemplateVersion: value => {
        safeSet((state: any) => {
          const newVersion =
            typeof value === "function" ? value(state.templateVersion) : value;
          return { templateVersion: newVersion };
        });

        const currentTemplateVersion = get().templateVersion;
        if (currentTemplateVersion && !isDestroyed) {
          get().updateTemplateVersion(currentUser.writeDraftState);
        }
      },
      setPersistingFields: updater =>
        safeSet((state: any) => ({
          persistingFields: updater(state.persistingFields),
        })),
      // Utility functions
      getModifyDate: params.getModifyDate || defaultGetModifyDate,
      simplified: params.simplified || false,
      onPublish: params.onPublish || (() => {}),
      onIsCompleted: params.onIsCompleted || (() => {}),
      markFieldsPersisting: (fields: Field[], requestId: string) => {
        safeSet((state: any) => {
          const newPersistingFields = { ...state.persistingFields };
          for (const field of fields) {
            if (!field) continue;
            const key = `${field.ownerId}::${field?.ownerType}::${field.fieldKey}`;
            if (!newPersistingFields[key]) {
              newPersistingFields[key] = [];
            }
            newPersistingFields[key]?.push(requestId);
          }
          return { persistingFields: newPersistingFields };
        });
      },
      markFieldPersisted: (fields: Field[], requestId: string) => {
        safeSet((state: any) => {
          const newPersistingFields = { ...state.persistingFields };
          for (const field of fields) {
            if (!field) continue;
            const key = `${field.ownerId}::${field?.ownerType}::${field.fieldKey}`;
            if (!newPersistingFields[key]) {
              newPersistingFields[key] = [];
            }
            newPersistingFields[key] = newPersistingFields[key]?.filter(
              (v: string) => v !== requestId
            );
          }
          return { persistingFields: newPersistingFields };
        });
      },
      // API operations with enhanced error handling
      saveFields: async (
        inputData: Record<string, Record<string, FieldInput>>,
        state: FieldState | null
      ) => {
        if (isDestroyed) return Promise.reject(new Error("Store destroyed"));

        // An empty save is a no-op, not an error — before #4889 the company
        // config still issued a mutation in this case, so nothing rejected.
        // Nothing catches this rejection (both callers re-throw from their
        // catch), which is how it surfaced as an unhandled promise rejection in
        // the business-applicant edit flow.
        if (!hasFieldsToWrite(inputData)) return null;

        const store = get();
        const query = config.saveFieldsQuery(
          inputData,
          store.templateQueryData,
          store.period
        );
        if (!query) return Promise.reject(new Error("no query"));

        const requestId = uuid();
        const inputFields = Object.values(inputData)
          .map(a => Object.values(a))
          .flat()
          .flat() as Field[];

        store.markFieldsPersisting(inputFields, requestId);
        safeSet({ saving: true });

        return client
          .mutate({
            mutation: query.query,
            variables: query.variables({ state }),
          })
          .finally(() => {
            setTimeout(() => {
              if (!isDestroyed) {
                store.markFieldPersisted(inputFields, requestId);
              }
            }, 300);
            safeSet({ saving: false });
          });
      },
      deleteFields: async (inputData: any, state: FieldState | null) => {
        if (isDestroyed) return Promise.reject(new Error("Store destroyed"));

        // Nothing to delete is a no-op — see saveFields above.
        if (!hasFieldsToWrite(inputData)) return null;

        const store = get();
        const query = config.deleteFieldsQuery(
          inputData,
          store.templateQueryData,
          store.period
        );
        if (!query) return Promise.reject(new Error("no query"));

        return client.mutate({
          mutation: query.query,
          variables: query.variables({ state }),
        });
      },
      updateTemplateVersion: async (
        state: types.FieldState.Draft | types.FieldState.MgrDraft | null
      ) => {
        if (isDestroyed) return Promise.reject(new Error("Store destroyed"));

        const store = get();

        if (!store.templateQueryData) return Promise.resolve(null);
        if (!currentUser.email || !store.templateQueryData)
          return Promise.reject(new Error("no current user or data"));

        const templateQuery = config.updateTemplateQuery(
          currentUser.email,
          store.name,
          store.templateVersion ||
            store.templateQueryData?.[store.ownerType]?.fieldData?.version,
          store.templateQueryData
        );

        if (!templateQuery)
          return Promise.reject(new Error("no template query"));

        return client.mutate({
          mutation: templateQuery.query,
          variables: templateQuery.variables({ state }),
        });
      },
      getRangerSuggestions: async ({
        keys,
        customPrompts,
        skipSave,
        ragLimits,
      }) => {
        if (isDestroyed) return null;

        const store = get();
        if (!store.templateData) return null;

        const ownerId = store.ownerId;
        const name = store.name;

        // Don't proceed if ownerId is null
        if (ownerId === null) {
          console.log("Cannot get ranger suggestions - ownerId is null");
          return null;
        }

        const res = await client.mutate({
          mutation: RANGER_DUE_DILIGENCE_DRAFTS,
          variables: {
            fieldKeys: keys || [
              ...Object.keys(store.templateData.fieldMeta.firm),
              ...Object.keys(store.templateData.fieldMeta.investment),
            ],
            investmentId:
              typeof ownerId === "string" ? parseInt(ownerId) : ownerId,
            customPromptsInput: customPrompts || [],
            ragLimits,
          },
        });

        if (!res?.data?.rangerDueDilligenceDrafts.id) return null;

        // Poll for task completion
        while (!isDestroyed) {
          const { data } = await client.query({
            query: GET_ONE_ASYNC_TASK,
            variables: { id: res.data.rangerDueDilligenceDrafts.id },
            fetchPolicy: "no-cache",
          });

          if (data?.asyncTaskStatus?.endDate) break;
          await new Promise(resolve => setTimeout(resolve, 3000));
        }

        if (isDestroyed) return null;

        const { data } = await client.query({
          query: GET_RANGER_DUE_DILIGENCE_DRAFTS,
          variables: { id: res.data.rangerDueDilligenceDrafts.id },
          fetchPolicy: "no-cache",
        });

        if (!data?.getRangerDueDiligenceDrafts) throw Error("No data");

        await store.fetchRangerFieldData();

        await fetch(data.getRangerDueDiligenceDrafts)
          .then(res => res.blob())
          .then(blob => {
            if (!skipSave && !isDestroyed) {
              saveAs(blob, `rangerSuggestions-${ownerId}-${name}.xlsx`);
            }
          });

        return null;
      },
      getDefaultPrompt: async (fieldKey: string) => {
        return await client
          .query({
            query: GET_DEFAULT_PROMPT,
            variables: { fieldKey },
            fetchPolicy: "no-cache",
          })
          .then(result => {
            return result.data?.storedPromptFromFieldKey?.prompt || null;
          });
      },

      // Data fetching methods
      fetchTemplateData: async () => {
        console.log("fetchTemplateData called, isDestroyed:", isDestroyed);
        if (isDestroyed) return null;

        const store = get();
        try {
          safeSet({ loading: true });
          const result = await client.query({
            query: config.templateQuery,
            variables: {
              id: store.ownerId,
              name: store.name,
              state: null,
              version: store.templateVersion,
            },
            fetchPolicy: "network-only",
          });

          if (isDestroyed) return null;

          const templateData = config.getTemplateData(result.data);
          const entityOwnerIds = config.getEntityOwnerIds(result.data);
          const fieldSet = templateData?.fieldSet as
            | types.GenericFieldSet
            | undefined;
          const content = fieldSet?.content as ContentItem[] | undefined;
          const hideImageUpload = fieldSet?.config?.hide_image_upload || false;

          // Initialize template version if not set
          const defaultVersion =
            result.data?.[store.ownerType]?.fieldData?.version;
          const currentTemplateVersion =
            store.templateVersion || defaultVersion;

          const requiredFields = [];
          if (templateData) {
            for (const [k, v] of Object.entries(
              templateData.fieldMeta.investment
            )) {
              if ((v as { required?: boolean }).required) {
                requiredFields.push(k);
              }
            }
          }

          safeSet({
            templateQueryData: result.data,
            templateData,
            entityOwnerIds,
            content: content || [],
            hideImageUpload,
            displayName: fieldSet?.display_name || "",
            requiredFields,
            defaultVersion,
            templateVersion: currentTemplateVersion, // Set this explicitly
            availableVersions:
              result.data?.[store.ownerType]?.templateOptions || [],
            error: undefined,
            loading: false,
          });

          return result.data;
        } catch (error) {
          safeSet({ error, loading: false });
          return null;
        }
      },

      watchFieldData: async () => {
        if (isDestroyed) return;

        const store = get();

        // Wait for template data to be fully loaded
        if (!store.templateData || !store.templateQueryData) {
          let retries = 0;
          const maxRetries = 50;

          const waitForData = () => {
            return new Promise<void>((resolve, reject) => {
              const checkData = () => {
                const currentStore = get();
                if (
                  currentStore.templateData &&
                  currentStore.templateQueryData
                ) {
                  resolve();
                } else if (retries >= maxRetries) {
                  reject(new Error("Template data failed to load"));
                } else {
                  retries++;
                  setTimeout(checkData, 100);
                }
              };
              checkData();
            });
          };

          try {
            await waitForData();
          } catch (error) {
            safeSet({ error, loading: false });
            return;
          }
        }

        // Continue with the rest of the function...
        const dataQuery = config.getTemplateDataQuery(
          store.name,
          store.templateQueryData,
          store.period
        );

        if (dataQuery.query === NO_QUERY) {
          safeSet({ loading: false });
          return;
        }

        try {
          safeSet({ loading: true });

          // Create watchQuery observables
          const serverPublishedState = config.getPublishedFieldState?.(store.templateQueryData);
          const effectivePublishState = (serverPublishedState !== undefined
            ? serverPublishedState
            : currentUser.writePublishState) as FieldState | null;

          const publishedQuery = client.watchQuery({
            query: dataQuery.query,
            variables: dataQuery.variables({
              state: effectivePublishState,
            }),
            fetchPolicy: "cache-and-network",
            pollInterval: 60000,
          });

          const managerQuery = client.watchQuery({
            query: dataQuery.query,
            variables: dataQuery.variables({
              state: types.FieldState.Mgr,
            }),
            fetchPolicy: "cache-and-network",
            pollInterval: 60000,
          });

          const rangerQuery = client.watchQuery({
            query: dataQuery.query,
            variables: dataQuery.variables({
              state: types.FieldState.RangerGenerated,
            }),
            fetchPolicy: "cache-and-network",
            pollInterval: 60000,
          });

          // Subscribe to published data
          const publishedSub = publishedQuery.subscribe({
            next: result => {
              if (isDestroyed) return;
              const publishedFieldsData = config.getFieldsData(result.data);
              safeSet((state: any) => ({
                ...state,
                publishedFieldData: result,
                publishedData: publishedFieldsData,
                loading: false,
                error: undefined,
              }));
            },
            error: error => {
              safeSet({ error, loading: false });
            },
          });

          // Subscribe to manager data
          const managerSub = managerQuery.subscribe({
            next: result => {
              if (isDestroyed) return;
              safeSet((state: any) => ({
                ...state,
                managerFieldData: result,
                loading: false,
                error: undefined,
              }));
            },
            error: error => {
              safeSet({ error, loading: false });
            },
          });

          // Subscribe to ranger data
          const rangerSub = rangerQuery.subscribe({
            next: result => {
              if (isDestroyed) return;
              safeSet((state: any) => ({
                ...state,
                rangerFieldData: result,
                loading: false,
                error: undefined,
              }));
            },
            error: error => {
              safeSet({ error, loading: false });
            },
          });

          querySubscriptions = [publishedSub, managerSub, rangerSub];
          const observables: ObservableQuery<any>[] = [
            publishedQuery,
            managerQuery,
            rangerQuery,
          ];

          // Handle edit data if in edit mode
          if (store.editMode) {
            const editQuery = client.watchQuery({
              query: dataQuery.query,
              variables: dataQuery.variables({
                state: currentUser.writeDraftState,
              }),
              fetchPolicy: "cache-and-network",
              pollInterval: 60000,
            });

            const editSub = editQuery.subscribe({
              next: result => {
                if (isDestroyed) return;
                const draftFieldsData = config.getFieldsData(result.data);
                safeSet((state: any) => ({
                  ...state,
                  editFieldData: result,
                  draftData: draftFieldsData,
                  loading: false,
                  error: undefined,
                }));
              },
              error: error => {
                safeSet({ error, loading: false });
              },
            });

            querySubscriptions.push(editSub);
            observables.push(editQuery);
          }

          registerRefetch(observables);
        } catch (error) {
          safeSet({ error, loading: false });
        }
      },

      fetchRangerFieldData:
        async (): Promise<ApolloQueryResult<any> | null> => {
          if (isDestroyed) return null;

          const store = get();
          if (!store.templateData || !store.templateQueryData) {
            return null;
          }

          const dataQuery = config.getTemplateDataQuery(
            store.name,
            store.templateQueryData,
            store.period
          );
          if (dataQuery.query === NO_QUERY) {
            return null;
          }

          try {
            const rangerData = await client.query({
              query: dataQuery.query,
              variables: dataQuery.variables({
                state: types.FieldState.RangerGenerated,
              }),
              fetchPolicy: "no-cache",
            });

            if (!isDestroyed) {
              safeSet({ rangerFieldData: rangerData });
            }
            return rangerData;
          } catch (error) {
            console.error("Failed to fetch ranger field data", error);
            return null;
          }
        },
      // Initialization
      initialize: async () => {
        if (isInitialized || isDestroyed) {
          console.log("Already initialized or destroyed, skipping.");
          return cleanup;
        }

        // Don't initialize if ownerId is null or invalid
        if (params.ownerId === null) {
          console.log("Skipping initialization - ownerId is null");
          return cleanup;
        }

        const ownerIdNum =
          typeof params.ownerId === "string"
            ? parseInt(params.ownerId)
            : params.ownerId;
        if (!ownerIdNum || ownerIdNum <= 0) {
          console.log(
            "Skipping initialization - invalid ownerId:",
            params.ownerId
          );
          return cleanup;
        }

        console.log("initialize called, isDestroyed:", isDestroyed);
        isInitialized = true;

        try {
          const data = await get().fetchTemplateData();
          if (data && !isDestroyed) {
            await get().watchFieldData();
          }
          return cleanup;
        } catch (error) {
          console.error("Initialization failed:", error);
          isInitialized = false;
          return () => {};
        }
      },
    };
  });
